import {
  type ComponentProps,
  type ComponentPropsWithRef,
  createElement,
  forwardRef,
  type Ref,
  useEffect,
  useState,
} from 'react'
import classnames from 'classnames'
import type { IIconProps, ISpinnerLoaderProps, ITooltipProps } from 'shared/ui'
import { Tooltip } from 'shared/ui/Tooltip'
import { SpinnerLoader } from 'shared/ui/Loader'
import { Emoji } from 'shared/ui/Emoji'
import { Icon } from 'shared/ui/Icon'
import type { IIconsVector } from 'shared/ui/Icon/icons'
import { type EmojiKeys } from 'shared/ui/Emoji/ui/types'
import { getAriaLabel, logger } from 'shared/lib'
import styles from './styles.module.scss'

export type IIconCircleButtonProps = ComponentProps<'div'> & {
  iconProps?: IIconProps
}

export type IIconButtonColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'tertiary2'
  | 'neutral'
  | 'transparent'
  | 'quaternary'
  | 'red'
  | 'green'
  | 'blue'
  | 'orange'
  | 'passivePrimary'
  | 'passiveSecondary'
  | 'noop'
  | 'lightSecondary'
  | 'darkPrimary'

export enum EnumIconButtonShape {
  Rounded = 'rounded',
  Square = 'square',
  None = 'none',
}

export type IIconButtonSize = 'medium' | 'small' | 'mini' | 'large' | 'origin_icon'

export type IIconButtonProps<T extends keyof HTMLElementTagNameMap = 'button'> =
  ComponentPropsWithRef<T> & {
    tag?: T
    icon?: IIconsVector
    iconProps?: IIconProps
    emoji?: EmojiKeys
    size?: IIconButtonSize
    color?: IIconButtonColor
    shape?: EnumIconButtonShape
    transparentBackground?: boolean
    transparentInactive?: boolean
    transparentColor?: boolean
    variant?: 'contained' | 'icon' | 'outlined' | 'custom' | 'transparent'
    intent?: 'destructive'
    fontSize?: number
    active?: boolean
    isEmoji?: boolean
    onDestroy?: () => void
    tooltipProps?: ITooltipProps
    disabled?: boolean
    loading?: boolean
    spinnerLoaderProps?: ISpinnerLoaderProps
    fullWidth?: boolean
    iconCircleButtonProps?: IIconCircleButtonProps
    ariaLabel: string
    withUiLoading?: boolean
  }

export type IIconButton = <T extends keyof HTMLElementTagNameMap = 'button'>(
  props: IIconButtonProps<T>
) => JSX.Element | null

export const IconButton = forwardRef(function IconButtonForwardRef<
  T extends keyof HTMLElementTagNameMap = 'button'
>(
  {
    tag,
    intent,
    icon = 'home',
    size = 'medium',
    color = 'primary',
    variant = 'contained',
    shape = EnumIconButtonShape.Square,
    tooltipProps,
    fontSize,
    emoji,
    active,
    className,
    transparentBackground,
    transparentInactive,
    transparentColor,
    onDestroy,
    loading: loadingProps,
    spinnerLoaderProps,
    fullWidth,
    iconProps,
    iconCircleButtonProps,
    disabled,
    ariaLabel,
    onClick,
    withUiLoading,
    ...props
  }: IIconButtonProps<T>,
  ref: Ref<HTMLElementTagNameMap[T]>
) {
  const defaultIconSize = size === 'small' ? 16 : 20
  const iconFontSize = fontSize || defaultIconSize
  const [uiLoading, setUiLoading] = useState<boolean>(false)
  useEffect(() => {
    return onDestroy
  }, [])

  const loading = uiLoading || loadingProps

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handleClick: IIconButtonProps<T>['onClick'] = async (e) => {
    if (withUiLoading) {
      try {
        setUiLoading(true)
        await onClick?.(e)
      } catch (e) {
        logger.error(e)
      } finally {
        setUiLoading(false)
      }
    } else {
      onClick?.(e)
    }
  }

  const renderContentButton = () => {
    if (loading) {
      const props =
        color === 'tertiary' && variant === 'icon'
          ? { determinatecolor: 'var(--gray-30)', indeterminatecolor: 'var(--gray-70)' }
          : { determinatecolor: 'var(--white-40)', indeterminatecolor: 'var(--white-100)' }
      return (
        <SpinnerLoader
          ariaLabel={'IconButton'}
          size={size === 'small' ? 14 : 16}
          {...props}
          {...spinnerLoaderProps}
        />
      )
    }

    if (!!emoji) {
      return <Emoji icon={emoji} fontSize={iconFontSize} />
    }

    return <Icon icon={icon} fontSize={iconFontSize} {...iconProps} />
  }

  const label = `IconButton_${ariaLabel}`
  const btn = createElement(
    tag ?? 'button',
    {
      ...props,
      className: classnames(
        styles.button,
        styles[size],
        styles[variant],
        styles[color],
        styles[shape],
        {
          [styles.transparentBackground]: transparentBackground,
          [styles.transparentInactive]: transparentInactive,
          [styles.transparentColor]: transparentColor,
          [styles.active]: active,
          [styles.fullWidth]: fullWidth,
          [styles.disabled]: disabled,
          [styles.loading]: loading,
          [styles[`intent${intent}`]]: intent,
        },
        className
      ),
      ref,
      'aria-label': getAriaLabel(label, 'default'),
      disabled,
      onClick: handleClick,
    },
    renderContentButton()
  )

  const circleBtn = () => {
    if (!iconCircleButtonProps) return

    const { iconProps, ...circleButtonProps } = iconCircleButtonProps

    return (
      <div
        className={classnames(
          styles.circleButton,
          styles.button,
          styles.circle,
          styles[variant],
          styles[color],
          active && styles.active,
          disabled && styles.disabled
        )}
        {...circleButtonProps}
        aria-label={getAriaLabel(label, 'circle')}
      >
        <Icon {...iconProps} fontSize={10} />
      </div>
    )
  }

  if (tooltipProps) {
    return (
      <div className={styles.wrap} aria-label={getAriaLabel(label, 'tooltip')}>
        <Tooltip {...tooltipProps} fullWidth={fullWidth}>
          {btn}
        </Tooltip>
        {circleBtn()}
      </div>
    )
  }
  return (
    <div className={styles.wrap} aria-label={getAriaLabel(label)}>
      {btn}
      {circleBtn()}
    </div>
  )
}) as IIconButton
